import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { LANDING_CATEGORIES, landingBySlug, type LandingPage as Landing } from "../data/landingPages";
import { useData } from "../context/DataContext";
import { useReveal } from "../hooks/useReveal";
import { buildPool, suggest } from "../engine/suggest";
import { PopButton } from "../components/PopButton";
import { NamesSpinBox, useHousemates } from "../components/seo/NamesSpinBox";
import { pickExcluding, sampleN, shuffle } from "../engine/random";
import { ResultReveal } from "../components/reveal/ResultReveal";
import { faqJsonLd, useSeo } from "../lib/seo";
import type { Category } from "../data/categories";

function SpinBox({ page, cat, onSpin, filters, setFilters }: { page: Landing; cat: Category; onSpin: () => void; filters: string[]; setFilters: (f: string[]) => void }) {
  const count = useMemo(() => buildPool(cat, { filters }).length, [cat, filters]);
  const toggle = (id: string) => setFilters(filters.includes(id) ? filters.filter((x) => x !== id) : [...filters, id]);
  return (
    <div className="rounded-[20px] border-[3px] border-[#FFC15E] bg-[#2A0F45] p-4 text-white" data-testid="landing-spin-box">
      <p className="ua-title text-2xl sm:text-3xl">{cat.question}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {cat.filters.map((f) => (
          <button key={f.id} className="chip !border-white/60 !bg-white/10 !text-white data-[active=true]:!bg-[#FFE14D] data-[active=true]:!text-[#3b1800]" data-active={filters.includes(f.id)} onClick={() => toggle(f.id)} data-testid={`landing-filter-${f.id}`}>{f.label}</button>
        ))}
      </div>
      <PopButton tone={cat.tone} size="xl" className="mt-4 w-full" disabled={!count} onClick={onSpin} data-testid="landing-spin-button">{page.spinLabel}</PopButton>
      <p className="mt-2 text-center text-xs font-bold text-white/70" data-testid="landing-pool-count">{count ? `Picking from ${count} ideas` : "No ideas match — loosen the filters."}</p>
    </div>
  );
}

function Links({ page }: { page: Landing }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="landing-links">
      {page.links.map((l, i) => (
        <Link key={l.to} to={l.to} data-testid={`landing-link-${i}`} className="ua-tile group flex items-center justify-between gap-2 border-[3px] border-[#FFB36B] bg-[#FFE0C2] p-2.5 text-[#3b1800]">
          <span><span className="block font-comic text-sm sm:text-base">{l.label}</span><span className="text-xs text-[#6b3a12]">{l.blurb}</span></span>
          <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" strokeWidth={3} />
        </Link>
      ))}
    </div>
  );
}

function Faq({ page }: { page: Landing }) {
  return (
    <div className="rounded-[20px] border-[3px] border-[#FFC15E] bg-white p-4 text-[#3b1800]" data-testid="landing-faq">
      <h2 className="ua-title text-2xl text-[#FF5D1F] sm:text-3xl">FAQ</h2>
      <div className="mt-2 grid items-start gap-1.5 md:grid-cols-2">
        {page.faqs.map((f, i) => (
          <details key={f.q} className="group rounded-xl border-2 border-[#FFD9A8] bg-[#FFF6E6] px-3 py-2" data-testid={`landing-faq-item-${i}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 font-comic text-sm"><h3>{f.q}</h3><ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" strokeWidth={3} /></summary>
            <p className="mt-1 text-sm text-[#6b3a12]">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage({ slug }: { slug: string }) {
  const page = landingBySlug(slug) as Landing;
  const cat = useMemo(() => LANDING_CATEGORIES[slug](), [slug]);
  const { prefs, recordDislike } = useData();
  const { spec, show, close } = useReveal();
  const [filters, setFilters] = useState<string[]>([]);
  const [names, setNames] = useHousemates();
  const [choreToo, setChoreToo] = useState(false);
  useSeo({ title: page.title, description: page.description, path: `/${slug}`, jsonLd: faqJsonLd(page.faqs) });

  const runNames = () => {
    const name = pickExcluding(names);
    const chore = choreToo ? pickExcluding(cat.items.map((i) => i.label)) : "";
    const wheel = shuffle([...names, ...names].slice(0, Math.max(4, names.length)));
    show({ kind: "category", title: chore ? "Who's on chore duty?" : cat.question, result: name, detail: chore ? `→ ${chore}` : "does the dishes!", anim: "wheel", reel: wheel, more: sampleN(names.filter((n) => n !== name), 3), category: cat.id });
  };
  const run = (exclude?: string) => {
    if (page.mode === "names") return runNames();
    try {
      const s = suggest(cat, { filters, dislikes: prefs.dislikes, exclude });
      show({ kind: "category", title: cat.question, result: s.result, anim: "wheel", reel: s.wheel, more: s.more, category: cat.id });
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-2 px-2 py-3 sm:space-y-3 sm:px-3" data-testid={`landing-page-${slug}`}>
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-extrabold uppercase tracking-wide hover:underline" data-testid="page-back-link"><ChevronLeft className="h-4 w-4" strokeWidth={3} /> Home</Link>
      <header>
        <h1 className="ua-logo text-4xl leading-none sm:text-6xl" data-testid="landing-h1">{page.h1}</h1>
        {page.subtitleH2
          ? <h2 className="mt-1 font-comic text-base text-[#6b3a12] sm:text-lg" data-testid="landing-h2">{page.subtitle}</h2>
          : <p className="mt-1 font-comic text-base text-[#6b3a12] sm:text-lg">{page.subtitle}</p>}
      </header>
      <div className="grid gap-2 sm:gap-3 md:grid-cols-[1.15fr_1fr]">
        {page.mode === "names"
          ? <NamesSpinBox page={page} cat={cat} names={names} setNames={setNames} choreToo={choreToo} setChoreToo={setChoreToo} onSpin={() => run()} />
          : <SpinBox page={page} cat={cat} onSpin={() => run()} filters={filters} setFilters={setFilters} />}
        <div className="rounded-[20px] border-[3px] border-[#FFC15E] bg-gradient-to-br from-[#FF7A1A] to-[#FF5D1F] p-4 text-white" data-testid="landing-intro">
          <h2 className="ua-title text-2xl sm:text-3xl">Let DITHER decide</h2>
          {page.intro.map((p) => <p key={p} className="mt-2 text-sm font-semibold">{p}</p>)}
        </div>
      </div>
      <Links page={page} />
      <Faq page={page} />
      <ResultReveal spec={spec} onClose={close}
        onAgain={() => { if (!spec) return; if (page.mode === "names") return runNames(); recordDislike(spec.result); run(spec.result); }} />
    </div>
  );
}
